import { useState, useMemo, useCallback, memo, useEffect } from 'react';
import {
  ComposableMap, Geographies, Geography, ZoomableGroup,
} from 'react-simple-maps';
import { ChevronRight, FileText } from 'lucide-react';
import { parseCountryList } from '../../../utils/dataProcessor';
import { getISO3, isGlobalSouthISO3 } from '../../../utils/countryMapping';
import { fetchTopoJSON, getTopoCache, geoToISO3 } from '../shared/constants';
import { ChartWrapper } from '../shared/ChartWrapper';
import { CountryModal } from '../../CountryModal';
import { usePartnershipData } from './usePartnershipData.jsx';
export const PartnershipMapChart = memo(function PartnershipMapChart({ results: resultsProp }) {
  const { localFilteredResults } = usePartnershipData();
  const results = resultsProp ?? localFilteredResults;
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [topology, setTopology] = useState(getTopoCache());

  useEffect(() => {
    if (!topology) {
      fetchTopoJSON().then(setTopology);
    }
  }, [topology]);

  const [countryModalOpen, setCountryModalOpen] = useState(false);
  const closeCountryModal = useCallback(() => setCountryModalOpen(false), []);

  const { iso3ToCounts, iso3ToPartnerCounts, highlightedISO3, maxCount, chartData, coverage, countryItems } = useMemo(() => {
    const counts = {};
    const partnerCounts = {};
    const iso3ToName = {};
    const highlighted = new Set();
    let withPartners = 0;
    let withGeo = 0;

    (results || []).forEach(result => {
      const preParsedPartners = [
        ...(result.partnersParsed || []),
        ...(result.partnersKPParsed || []),
      ];
      let partnerAcronyms;
      if (preParsedPartners.length > 0) {
        partnerAcronyms = new Set(preParsedPartners.map(p => p.acronym).filter(Boolean));
      } else {
        const partnerText = result.partners || result.partnersKP || '';
        if (!partnerText || partnerText === 'Not Applicable' || partnerText === 'Not applicable for KP') return;
        partnerAcronyms = new Set();
        partnerText.split('\u2022').map(s => s.trim()).filter(Boolean).forEach(entry => {
          const semiIdx = entry.indexOf(';');
          const namePart = semiIdx > -1 ? entry.substring(0, semiIdx).trim() : entry.trim();
          const dashIdx = namePart.indexOf(' - ');
          const acronym = dashIdx > -1 ? namePart.substring(0, dashIdx).trim() : namePart;
          if (acronym) partnerAcronyms.add(acronym);
        });
      }

      if (partnerAcronyms.size === 0) return;
      withPartners++;

      if (!result.countries && (!result.countriesParsed || result.countriesParsed.length === 0)) return;
      withGeo++;

      const countryEntries = result.countriesParsed || parseCountryList(result.countries);

      countryEntries.forEach(country => {
        const iso3 = getISO3(country);
        if (!iso3 || !isGlobalSouthISO3(iso3)) return;
        highlighted.add(iso3);
        counts[iso3] = (counts[iso3] || 0) + 1;
        if (!iso3ToName[iso3]) iso3ToName[iso3] = country;
        if (!partnerCounts[iso3]) partnerCounts[iso3] = new Set();
        partnerAcronyms.forEach(a => partnerCounts[iso3].add(a));
      });
    });

    const vals = Object.values(counts);
    const data = Object.entries(counts).map(([iso3, count]) => ({
      country_iso3: iso3,
      result_count: count,
      unique_partners: (partnerCounts[iso3] || new Set()).size,
    }));

    const items = Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .map(([iso3, count]) => ({
        name: iso3ToName[iso3] || iso3,
        count,
        partners: (partnerCounts[iso3] || new Set()).size,
      }));

    const pct = withPartners > 0 ? Math.round((withGeo / withPartners) * 100) : 0;

    return {
      iso3ToCounts: counts,
      iso3ToPartnerCounts: partnerCounts,
      highlightedISO3: highlighted,
      maxCount: vals.length > 0 ? Math.max(...vals) : 1,
      chartData: data,
      coverage: { withPartners, withGeo, pct },
      countryItems: items,
    };
  }, [results]);

  const getColor = (iso3) => {
    if (!highlightedISO3.has(iso3)) return '#E5E7EB';
    const count = iso3ToCounts[iso3] || 1;
    const t = maxCount > 1 ? (count - 1) / (maxCount - 1) : 0;
    const r = Math.round(191 + t * (0 - 191));
    const g = Math.round(219 + t * (125 - 219));
    const b = Math.round(254 + t * (188 - 254));
    return `rgb(${r},${g},${b})`;
  };

  const handleMouseEnter = (geo, evt) => {
    const iso3 = geoToISO3(geo);
    const name = geo.properties.NAME || geo.properties.name || iso3;
    if (highlightedISO3.has(iso3)) {
      const cnt = iso3ToCounts[iso3] || 0;
      const partnerSet = iso3ToPartnerCounts[iso3];
      const partnerCount = partnerSet ? partnerSet.size : 0;
      setTooltipContent(`${name}: ${cnt} result${cnt !== 1 ? 's' : ''} with partners (${partnerCount} unique partner${partnerCount !== 1 ? 's' : ''})`);
    } else {
      setTooltipContent(name);
    }
    setTooltipPos({ x: evt.clientX, y: evt.clientY });
  };

  const handleMouseLeave = () => setTooltipContent('');
  const handleMouseMove = (evt) => { if (tooltipContent) setTooltipPos({ x: evt.clientX, y: evt.clientY }); };

  if (!topology) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-center aspect-[16/7]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-3 border-[#007DBC] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-500">Loading map data...</p>
          </div>
        </div>
      </div>
    );
  }

  const hasData = highlightedISO3.size > 0;

  return (
    <ChartWrapper title="Partnership Geographic Footprint" info="Geographic distribution of results that have partner data only. Results without partners (e.g. knowledge products) are excluded, so fewer countries may appear compared to the Results by Location map in the Progress section." hideDescription chartData={chartData} sourceResults={results} exportFilename="partnership-geographic-footprint">
      <div className="relative aspect-[16/7]" onMouseMove={handleMouseMove}>
        <ComposableMap projection="geoEquirectangular" projectionConfig={{ rotate: [-10, 0, 0], scale: 130 }} width={800} height={350} style={{ width: '100%', height: '100%' }}>
          <ZoomableGroup>
            <Geographies geography={topology}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const iso3 = geoToISO3(geo);
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={getColor(iso3)}
                      stroke="#fff"
                      strokeWidth={0.5}
                      onMouseEnter={(evt) => handleMouseEnter(geo, evt)}
                      onMouseLeave={handleMouseLeave}
                      style={{
                        default: { outline: 'none' },
                        hover: { fill: highlightedISO3.has(iso3) ? '#005A9E' : '#D1D5DB', outline: 'none', cursor: 'pointer' },
                        pressed: { outline: 'none' },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
        {tooltipContent && (
          <div className="fixed z-50 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg shadow-lg pointer-events-none" style={{ left: tooltipPos.x + 12, top: tooltipPos.y - 28 }}>
            {tooltipContent}
          </div>
        )}
      </div>
      {hasData && (
        <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#BFDBFE' }} />
            <span>Fewer partnered results</span>
          </div>
          <div className="w-12 h-3 rounded" style={{ background: 'linear-gradient(to right, #BFDBFE, #007DBC)' }} />
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#007DBC' }} />
            <span>More partnered results</span>
          </div>
        </div>
      )}

      {/* Description — rendered manually so it appears before coverage bar */}
      <div className="mt-4 pt-3 border-t border-[var(--border-secondary)]">
        <div className="flex items-start gap-2">
          <FileText className="w-4 h-4 text-[var(--text-tertiary)] mt-0.5 flex-shrink-0" />
          <p className="text-sm italic text-[var(--text-secondary)]">
            Shows only results that have partner data — knowledge products and other results without partners are excluded. Countries are shaded by the number of partnered results, so fewer countries may appear here than in the Results by Location map.
          </p>
        </div>
      </div>

      {/* Geographic coverage bar */}
      {coverage.withPartners > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-[var(--text-secondary)] mb-1.5">
            <span>Geographic coverage of partnered results</span>
            <span>{coverage.withGeo.toLocaleString()} of {coverage.withPartners.toLocaleString()} ({coverage.pct}%)</span>
          </div>
          <div className="flex h-3 rounded-full overflow-hidden bg-gray-200">
            <div className="bg-[#007DBC] transition-all" style={{ width: `${coverage.pct}%` }} />
            <div className="bg-gray-300" style={{ width: `${100 - coverage.pct}%` }} />
          </div>
          <div className="flex items-center gap-4 mt-1.5 text-[10px] text-[var(--text-tertiary)]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#007DBC] inline-block" /> With country data</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300 inline-block" /> No country data</span>
          </div>
        </div>
      )}

      {/* Top countries + view all */}
      {countryItems.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[var(--border-primary)]">
          <div className="flex flex-wrap gap-2 items-center">
            {countryItems.slice(0, 10).map(({ name, count, partners }) => (
              <span
                key={name}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-[var(--surface-tertiary)] text-[var(--text-secondary)] text-xs rounded-full"
              >
                {name}
                <span className="text-[10px] font-semibold text-[var(--text-tertiary)]">{count}</span>
              </span>
            ))}
            {countryItems.length > 10 && (
              <button
                onClick={() => setCountryModalOpen(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#007DBC]/10 text-[#007DBC] text-xs font-medium rounded-full hover:bg-[#007DBC]/20 transition-colors"
              >
                View all {countryItems.length} countries
                <ChevronRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      )}

      {countryModalOpen && <CountryModal items={countryItems} onClose={closeCountryModal} />}
    </ChartWrapper>
  );
});
